// src/pages/dashboard/DashboardHome.tsx
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../context/AuthContext';
import { useUserDisplayName } from '../../hooks/useUserDisplayName';
import { useWindow } from '../../context/WindowContext';
import { formatCurrency } from '../../lib/utils';
import { WalletGrid } from '../../components/dashboard/wallet-grid';
import { useTranslation } from 'react-i18next';
// import NewAlert from '../../components/dashboard/NewAlert';
import type { RootState } from '../../store';

export default function DashboardHome() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { windowWidth } = useWindow();
  const { displayName } = useUserDisplayName();
  const { t } = useTranslation();

  const { data: { mainWallets, cryptoWallets, masterCard, zenusAccount }, status } = useSelector((state: RootState) => state.assets);
  const { data: { cryptoPrices } } = useSelector((state: RootState) => state.setting);

  const [isNewOpen, setIsNewOpen] = useState<boolean>(false);
  const [solPrice, setSolPrice] = useState<number>(0);
  const [usdtPrice, setUsdtPrice] = useState<number>(1);

  // Calculate real crypto balances from all wallets and tokens
  const calculateCryptoBalance = () => {
    let total = 0;

    // Add custodian wallets balance (already in USD)
    total += mainWallets?.custodianWallets?.balance || 0;

    // Calculate from actual crypto wallets
    if (cryptoWallets?.data) {
      // Iterate through each network
      Object.keys(cryptoWallets.data).forEach(network => {
        const walletsInNetwork = cryptoWallets.data[network];

        if (Array.isArray(walletsInNetwork)) {
          walletsInNetwork.forEach((wallet: any) => {
            // Process balances for each wallet
            if (wallet.balances) {
              Object.keys(wallet.balances).forEach(currency => {
                const balance = parseFloat(wallet.balances[currency]) || 0;

                if (balance > 0) {
                  let usdValue = 0;

                  switch(currency.toUpperCase()) {
                    case 'BTC':
                    case 'BITCOIN':
                      usdValue = balance * (cryptoPrices?.BTC || 0);
                      break;
                    case 'ETH':
                    case 'ETHEREUM':
                      usdValue = balance * (cryptoPrices?.ETH || 0);
                      break;
                    case 'BNB':
                      usdValue = balance * (cryptoPrices?.BNB || 0);
                      break;
                    case 'TRX':
                    case 'TRON':
                      usdValue = balance * (cryptoPrices?.TRX || 0);
                      break;
                    case 'SOL':
                    case 'SOLANA':
                      usdValue = balance * (solPrice || 145);
                      break;
                    case 'USDT':
                    case 'USDT_ERC20':
                    case 'USDT_TRC20':
                    case 'USDT_BEP20':
                    case 'USDC':
                    case 'USDC_ERC20':
                    case 'USDC_TRC20':
                    case 'USDC_BEP20':
                      // Stablecoins are 1:1 with USD
                      usdValue = balance;
                      break;
                    default:
                      // For unknown currencies, treat as USD
                      usdValue = balance;
                  }

                  total += usdValue;
                }
              });
            }
          });
        }
      });
    }

    return total;
  };

  // Calculate balances (include Zenus USD account)
  const fiatBalance = (mainWallets?.fiatWallets?.balance || 0) + (zenusAccount?.balance || 0);

  // MasterCard balance is ALREADY converted to USD in Redux (assetsReducer line 85)
  // Do NOT convert again here or it will be doubled!
  const cardBalanceUSD = masterCard?.balance || 0;

  // Calculate total balances
  const totalFiat = fiatBalance;
  const totalCrypto = calculateCryptoBalance();
  const totalCard = cardBalanceUSD;  // Already in USD from Redux
  const totalWealth = totalFiat + totalCrypto + totalCard;

  const content = "Coming soon!";

  const isDesktopView = windowWidth >= 1200;
  const isFastAccount = user?.currentPhase === 'ONBOARDING_COMPLETE_FAST';

  // Fetch SOL and USDT prices
  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        // Using CoinGecko API (free tier)
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=solana,tether&vs_currencies=usd'
        );
        const data = await response.json();

        if (data.solana?.usd) {
          setSolPrice(data.solana.usd);
        }
        if (data.tether?.usd) {
          setUsdtPrice(data.tether.usd);
        }
      } catch (error) {
        console.error('Error fetching crypto prices:', error);
        // Fallback prices
        setSolPrice(145.00);
        setUsdtPrice(1.00);
      }
    };

    fetchCryptoPrices();
    // Update prices every 10 minutes (matching the UI text)
    const interval = setInterval(fetchCryptoPrices, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (location.pathname == "/dashboard/settings" || location.pathname == "/dashboard/myprofile" || location.pathname == "/dashboard/support") {
      setIsNewOpen(true);
    }
  }, [location.pathname])

  const setNews = () => {
    setIsNewOpen(false)
    navigate('/dashboard');
  }
  return (
    <div className='p-3 sm:!px-10  h-full max-w-full relative'>
      {/* Contenido principal */}
      <div className='mb-5'>
        <h1 className='text-4xl mb-1 font-medium'>{t('dashboard.welcomeBack')}, {displayName}.</h1>
        <p className='text-3xl text-gray-700 font-normal'>{t('dashboard.title')}</p>
      </div>

      <div className='mb-5'>
        <h2 className='text-[18px] mb-[10px] text-[#333] font-medium'>{t('dashboard.totalAssets')}</h2>
        <div className='text-[3.5rem] font-semibold text-[#B3A369] mb-[10px]'>
          {(status === "idle" || status === "loading")
            ? <span style={{ fontSize: '40px' }}>{t('common.loading')}</span>
            : formatCurrency(totalWealth)
          }
        </div>

        <div className='flex gap-7 flex-wrap items-center justify-start relative sm:gap-20 sm:flex-row'>
          <div className='flex flex-wrap gap-10 sm:gap-20'>
            <div>
              <h3 className='text-base text-[#666] mb-2'>{t('dashboard.fiatWallets')}</h3>
              <p className='text-2xl text-[#333] font-medium'>
                {(status === "idle" || status === "loading")
                  ? '...'
                  : formatCurrency(totalFiat)
                }
              </p>
            </div>
            <div>
              <h3 className='text-base text-[#666] mb-2'>{t('dashboard.cryptoWallets')}</h3>
              <p className='font-medium text-[#333] text-2xl'>
                {(status === "idle" || status === "loading")
                  ? '...'
                  : formatCurrency(totalCrypto)
                }
              </p>
            </div>
            <div>
              <h3 className='text-base text-[#666] mb-2'>Cards</h3>
              <p className='font-medium text-[#333] text-2xl'>
                {(status === "idle" || status === "loading")
                  ? '...'
                  : formatCurrency(totalCard)
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Crypto Prices - 15cm a la derecha en PC */}
      {windowWidth > 425 ? (
        <div className='absolute top-64 left-[calc(50%+15rem)] transform -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg border border-gray-200 z-10'>
          <h3 className='text-base text-[#666] mb-2 flex items-center gap-2'>
            {t('dashboard.cryptoPrices')}
            <span className='text-xs text-[#999]'>({t('dashboard.updatesEvery10Min')})</span>
          </h3>
          <div className='grid grid-cols-2 gap-2 text-sm'>
            <div><span className='font-medium text-belobaba-gold'>BTC:</span> {formatCurrency(cryptoPrices.BTC)}</div>
            <div><span className='font-medium text-belobaba-gold'>ETH:</span> {formatCurrency(cryptoPrices.ETH)}</div>
            <div><span className='font-medium text-belobaba-gold'>TRX:</span> {formatCurrency(cryptoPrices.TRX)}</div>
            <div><span className='font-medium text-belobaba-gold'>BNB:</span> {formatCurrency(cryptoPrices.BNB)}</div>
            <div><span className='font-medium text-belobaba-gold'>SOL:</span> {formatCurrency(solPrice)}</div>
            <div><span className='font-medium text-belobaba-gold'>USDT:</span> {formatCurrency(usdtPrice)}</div>
          </div>
        </div>
      ) : null}

      {/* Precios para móvil - se muestran antes del WalletGrid */}
      {windowWidth <= 425 && (
        <div className='mb-6'>
          {/* Precios móvil */}
          <div className='bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg border border-gray-200'>
            <h3 className='text-base text-[#666] mb-2 flex items-center gap-2'>
              Current Crypto Prices
              <span className='text-xs text-[#999]'>(Updates every 10 min)</span>
            </h3>
            <div className='grid grid-cols-2 gap-2 text-sm'>
              <div><span className='font-medium text-belobaba-gold'>BTC:</span> {formatCurrency(cryptoPrices.BTC)}</div>
              <div><span className='font-medium text-belobaba-gold'>ETH:</span> {formatCurrency(cryptoPrices.ETH)}</div>
              <div><span className='font-medium text-belobaba-gold'>TRX:</span> {formatCurrency(cryptoPrices.TRX)}</div>
              <div><span className='font-medium text-belobaba-gold'>BNB:</span> {formatCurrency(cryptoPrices.BNB)}</div>
              <div><span className='font-medium text-belobaba-gold'>SOL:</span> {formatCurrency(solPrice)}</div>
              <div><span className='font-medium text-belobaba-gold'>USDT:</span> {formatCurrency(usdtPrice)}</div>
            </div>
          </div>
        </div>
      )}

      <div className='max-w-full overflow-hidden'>
        <WalletGrid />
      </div>
      {/* {isNewOpen && (
        <NewAlert isOpen={isNewOpen} onClose={setNews} content={content} />
      )} */}
    </div>
  );
}